import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import {
    AlertTriangle, RefreshCw, Mail, Zap, X, CreditCard, Clock,
    ChevronRight, CheckCircle2, XCircle,
} from "lucide-react";
import { formatCurrency, formatDateTime, relativeTime } from "@/lib/format";

const STAGE_LABEL = {
    1: "Faza 1 · Përpjekja 1",
    2: "Faza 2 · Përpjekja 2",
    3: "Faza 3 · Përpjekja finale",
    4: "Finale · E vonuar",
};

const STAGE_COLOR = {
    1: "bg-amber-50 text-amber-700 border-amber-200",
    2: "bg-orange-50 text-orange-700 border-orange-200",
    3: "bg-rose-50 text-rose-700 border-rose-200",
    4: "bg-red-100 text-red-800 border-red-300",
};

export default function SuperDunning() {
    const [rows, setRows] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [stageFilter, setStageFilter] = useState("");
    const [selected, setSelected] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = stageFilter ? `?stage=${stageFilter}` : "";
            const [r, k] = await Promise.all([
                api.get(`/saas/dunning/at-risk${params}`),
                api.get(`/saas/dunning/kpis`),
            ]);
            setRows(r.data);
            setKpis(k.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* stageFilter dep */ }, [stageFilter]);

    const sendFollowup = async (inv) => {
        if (!window.confirm(`Dërgo email kujtese te ${inv.hotel_name}?`)) return;
        setBusyId(inv.invoice_id);
        try {
            const { data } = await api.post(`/saas/dunning/invoices/${inv.invoice_id}/send-followup`);
            if (data.delivered) {
                toast.success("Emaili u dërgua");
            } else {
                toast.warning("Emaili u dërgua por s'u konfirmua dorëzimi");
            }
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusyId(null);
        }
    };

    const forceRetry = async (inv) => {
        if (!inv.has_card) {
            toast.error("Hoteli nuk ka kartë të ruajtur — s'mund të tarifohet");
            return;
        }
        if (!window.confirm(
            `Provo tarifimin për ${inv.hotel_name}?\n` +
            `Shuma: ${formatCurrency(inv.amount_due, inv.currency)}\n` +
            `Karta: •••• ${inv.card_last4 || "—"}\n\n` +
            `Kjo do të tentojë të tarifojë kartën menjëherë.`
        )) return;
        setBusyId(inv.invoice_id);
        try {
            const { data } = await api.post(`/saas/dunning/invoices/${inv.invoice_id}/force-retry`);
            if (data.ok) {
                toast.success(`U tarifua me sukses · Fatura ${inv.invoice_number} u pagua`);
            } else if (data.error === "authentication_required") {
                toast.warning("Kërkohet 3-D Secure — s'mund të tarifohet off-session");
            } else {
                toast.error(`Tarifimi dështoi: ${data.message || data.error || "Gabim i panjohur"}`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusyId(null);
        }
    };

    const stageOfRow = (r) => {
        const s = Number(r.dunning_stage || 0);
        return s >= 4 ? 4 : s;
    };

    return (
        <>
            <Topbar
                title="Paneli i Dunning-ut"
                subtitle="Faturat në rrezik, tarifime karte të dështuara dhe abonime të vonuara"
            />
            <div className="flex-1 overflow-auto p-6" data-testid="super-dunning">
                <DunningConfigCard />
                <KpiStrip kpis={kpis} loading={loading} />

                <div className="mt-4 mb-3 flex flex-wrap items-center gap-2">
                    <FilterChip active={stageFilter === ""} onClick={() => setStageFilter("")} testid="dunning-filter-all">
                        Të gjitha në rrezik {kpis && <span className="ml-1 opacity-70">{kpis.at_risk_count}</span>}
                    </FilterChip>
                    <FilterChip active={stageFilter === "1"} onClick={() => setStageFilter("1")} testid="dunning-filter-1">
                        Faza 1 {kpis && <span className="ml-1 opacity-70">{kpis.by_stage.stage1}</span>}
                    </FilterChip>
                    <FilterChip active={stageFilter === "2"} onClick={() => setStageFilter("2")} testid="dunning-filter-2">
                        Faza 2 {kpis && <span className="ml-1 opacity-70">{kpis.by_stage.stage2}</span>}
                    </FilterChip>
                    <FilterChip active={stageFilter === "3"} onClick={() => setStageFilter("3")} testid="dunning-filter-3">
                        Faza 3 {kpis && <span className="ml-1 opacity-70">{kpis.by_stage.stage3}</span>}
                    </FilterChip>
                    <FilterChip active={stageFilter === "4"} onClick={() => setStageFilter("4")} testid="dunning-filter-4">
                        Finale / E vonuar {kpis && <span className="ml-1 opacity-70">{kpis.by_stage.final}</span>}
                    </FilterChip>
                    <div className="ml-auto">
                        <button onClick={load} disabled={loading}
                            className="akm-btn-ghost h-8 text-[12px] disabled:opacity-50"
                            data-testid="dunning-refresh">
                            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Rifresko
                        </button>
                    </div>
                </div>

                <div className="akm-card overflow-hidden">
                    <table className="akm-table w-full">
                        <thead>
                            <tr>
                                <th className="text-left">Hoteli</th>
                                <th className="text-left">Fatura</th>
                                <th className="text-left">Shuma</th>
                                <th className="text-left">Faza</th>
                                <th className="text-left">Karta</th>
                                <th className="text-left">Gabimi i fundit</th>
                                <th className="text-left">Përpjekja tjetër</th>
                                <th className="text-right w-[260px]">Veprimet</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan="8" className="py-16 text-center text-muted-foreground">
                                        <CheckCircle2 size={40} className="mx-auto mb-2 text-emerald-500" />
                                        <div className="text-[14px] font-semibold text-slate-700">Asnjë faturë në rrezik</div>
                                        <div className="mt-1 text-[12px]">Të gjitha abonimet aktive po paguhen në kohë.</div>
                                    </td>
                                </tr>
                            )}
                            {rows.map((r) => {
                                const stage = stageOfRow(r);
                                return (
                                    <tr key={r.invoice_id} data-testid={`dunning-row-${r.invoice_id}`}>
                                        <td>
                                            <div className="font-medium text-slate-900">{r.hotel_name || "—"}</div>
                                            <div className="text-[11px] text-muted-foreground">{r.billing_email || "no billing email"}</div>
                                        </td>
                                        <td>
                                            <div className="font-mono text-[11.5px] text-slate-800">{r.invoice_number}</div>
                                            <div className="text-[11px] text-muted-foreground">
                                                Përpjekje: <b>{r.charge_attempts}</b>
                                            </div>
                                        </td>
                                        <td className="font-mono">{formatCurrency(r.amount_due, r.currency)}</td>
                                        <td>
                                            <span className={`inline-block rounded-md border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${STAGE_COLOR[stage] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                                                {STAGE_LABEL[stage] || `Stage ${stage}`}
                                            </span>
                                            {r.sub_status && r.sub_status !== "active" && (
                                                <div className="mt-1 text-[10px] font-semibold uppercase text-red-700">
                                                    Abonimi: {r.sub_status}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {r.has_card ? (
                                                <div className="text-[12px] text-slate-700">
                                                    <CreditCard size={11} className="mr-1 inline text-slate-400" />
                                                    <span className="uppercase">{r.card_brand || "karta"}</span>
                                                    <span className="ml-1 font-mono">•• {r.card_last4 || "----"}</span>
                                                </div>
                                            ) : (
                                                <div className="text-[11px] text-red-700 font-semibold">Pa kartë</div>
                                            )}
                                        </td>
                                        <td className="max-w-[220px]">
                                            <div className="truncate text-[12px] text-slate-700" title={r.last_charge_error || ""}>
                                                {r.last_charge_error || "—"}
                                            </div>
                                            {r.last_charge_code && (
                                                <div className="text-[10.5px] font-mono text-muted-foreground">{r.last_charge_code}</div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="text-[12px] text-slate-700">
                                                {r.next_charge_retry_at ? formatDateTime(r.next_charge_retry_at) : "—"}
                                            </div>
                                            {r.last_charge_at && (
                                                <div className="text-[10.5px] text-muted-foreground">
                                                    E fundit: {relativeTime(r.last_charge_at)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <div className="inline-flex flex-wrap justify-end gap-1">
                                                <button
                                                    onClick={() => sendFollowup(r)}
                                                    disabled={busyId === r.invoice_id}
                                                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-[#ff7b02] hover:text-[#ff7b02] disabled:opacity-50"
                                                    data-testid={`dunning-followup-${r.invoice_id}`}
                                                >
                                                    <Mail size={11} className="inline -mt-0.5" /> Dërgo kujtesë
                                                </button>
                                                <button
                                                    onClick={() => forceRetry(r)}
                                                    disabled={busyId === r.invoice_id || !r.has_card}
                                                    className="rounded-md bg-[#ff7b02] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#e56d00] disabled:cursor-not-allowed disabled:opacity-50"
                                                    data-testid={`dunning-retry-${r.invoice_id}`}
                                                    title={!r.has_card ? "Nuk ka kartë të ruajtur" : "Tarifo tani (off-session)"}
                                                >
                                                    <Zap size={11} className="inline -mt-0.5" /> {busyId === r.invoice_id ? "Duke tarifuar…" : "Provo tarifimin"}
                                                </button>
                                                <button
                                                    onClick={() => setSelected(r)}
                                                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                                                    data-testid={`dunning-detail-${r.invoice_id}`}
                                                >
                                                    <ChevronRight size={11} className="inline" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && (
                <DetailDrawer row={selected} onClose={() => setSelected(null)} />
            )}
        </>
    );
}

function FilterChip({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`rounded-md border px-2.5 py-1 text-[12px] font-medium transition ${
                active
                    ? "border-[#ff7b02] bg-[#ff7b02] text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            }`}
        >
            {children}
        </button>
    );
}

function KpiStrip({ kpis, loading }) {
    const cards = useMemo(() => ([
        { label: "Fatura në rrezik", value: kpis?.at_risk_count ?? "—", tone: "amber", icon: AlertTriangle },
        { label: "Shuma në rrezik", value: kpis ? formatCurrency(kpis.total_at_risk_amount) : "—", tone: "amber" },
        { label: "Të vonuara", value: kpis?.past_due_count ?? "—", tone: "red", icon: XCircle },
        { label: "Shuma e vonuar", value: kpis ? formatCurrency(kpis.total_past_due_amount) : "—", tone: "red" },
        { label: "Hotele të prekur", value: kpis?.hotels_impacted ?? "—", tone: "slate" },
        { label: "Faza finale", value: kpis?.by_stage?.final ?? "—", tone: "red", icon: Clock },
    ]), [kpis]);
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="dunning-kpi-strip">
            {cards.map((c) => (
                <div key={c.label} className="akm-card p-3.5">
                    <div className="flex items-center justify-between">
                        <div className="akm-section-title">{c.label}</div>
                        {c.icon && <c.icon size={14} className={
                            c.tone === "amber" ? "text-amber-600" :
                            c.tone === "red" ? "text-red-600" : "text-slate-400"
                        } />}
                    </div>
                    <div className={`mt-1 text-xl font-semibold tracking-tight ${
                        c.tone === "amber" ? "text-amber-700" :
                        c.tone === "red" ? "text-red-700" : "text-slate-800"
                    } ${loading ? "opacity-50" : ""}`}>
                        {c.value}
                    </div>
                </div>
            ))}
        </div>
    );
}

function DetailDrawer({ row, onClose }) {
    return (
        <div className="fixed inset-0 z-50" data-testid="dunning-detail-drawer">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute right-0 top-0 h-full w-[560px] max-w-full overflow-y-auto bg-white shadow-2xl">
                <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Detaje Dunning</div>
                        <div className="text-[15px] font-semibold text-slate-900">{row.hotel_name}</div>
                    </div>
                    <button onClick={onClose} className="akm-btn-ghost h-8 w-8" data-testid="dunning-drawer-close">
                        <X size={14} />
                    </button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="grid grid-cols-2 gap-3 text-[13px]">
                        <Field label="Fatura">
                            <span className="font-mono">{row.invoice_number}</span>
                        </Field>
                        <Field label="Shuma e detyruar">
                            <span className="font-mono">{formatCurrency(row.amount_due, row.currency)}</span>
                        </Field>
                        <Field label="Përpjekje">
                            <b>{row.charge_attempts}</b> / 3
                        </Field>
                        <Field label="Faza">
                            <span className="font-semibold">{STAGE_LABEL[row.dunning_stage >= 4 ? 4 : row.dunning_stage] || `—`}</span>
                        </Field>
                        <Field label="Statusi i abonimit">
                            <span className="uppercase">{row.sub_status || "—"}</span>
                        </Field>
                        <Field label="Karta">
                            {row.has_card ? `${row.card_brand || "karta"} •• ${row.card_last4 || "----"}` : "Pa kartë"}
                        </Field>
                    </div>

                    <div>
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            Gabimi i fundit nga Stripe
                        </div>
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800">
                            {row.last_charge_error || "Ende s'ka gabim të regjistruar"}
                            {row.last_charge_code && (
                                <div className="mt-1 font-mono text-[11px] text-red-700">kodi: {row.last_charge_code}</div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            Kronologjia
                        </div>
                        {(!row.dunning_events || row.dunning_events.length === 0) ? (
                            <div className="text-[12px] text-muted-foreground">Ende s&apos;ka ngjarje Dunning të regjistruara.</div>
                        ) : (
                            <ol className="space-y-2">
                                {row.dunning_events.slice().reverse().map((ev, i) => (
                                    <li key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                                        <div className="flex items-center justify-between">
                                            <span className={`text-[10.5px] font-bold uppercase tracking-wide ${
                                                (ev.stage >= 4) ? "text-red-700" : "text-amber-700"
                                            }`}>
                                                Faza {ev.stage} {ev.manual ? "· manuale" : ""}
                                            </span>
                                            <span className="text-[10.5px] text-muted-foreground">{formatDateTime(ev.at)}</span>
                                        </div>
                                        <div className="mt-1 text-[12px] text-slate-700">{ev.error_message}</div>
                                        {ev.error_code && (
                                            <div className="mt-0.5 font-mono text-[10.5px] text-muted-foreground">
                                                {ev.error_code}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-0.5 text-[13px] text-slate-800">{children}</div>
        </div>
    );
}


// ─── Konfigurimi i Dunning-ut (grace period, retry, reminder) ──────────
function DunningConfigCard() {
    const [cfg, setCfg] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/saas/dunning/config");
            setCfg(data);
        } catch { /* silent */ }
    };
    useEffect(() => { load(); }, []);

    const save = async (updates) => {
        setSaving(true);
        try {
            const { data } = await api.put("/saas/dunning/config", updates);
            setCfg(data);
            setEditing(false);
            toast.success("Konfigurimi u ruajt");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gabim");
        } finally { setSaving(false); }
    };

    if (!cfg) return null;
    return (
        <div className="akm-card mb-4 p-4" data-testid="dunning-config-card">
            <div className="mb-2 flex items-center justify-between">
                <div>
                    <h2 className="text-[14px] font-bold text-slate-900">Konfigurimi i Dunning-ut</h2>
                    <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                        Kontrollo se sa përpjekje bëhen për tarifim, sa herë dërgohen kujtesat dhe kur pezullohet aksesi.
                    </p>
                </div>
                <button
                    onClick={() => setEditing((v) => !v)}
                    data-testid="dunning-config-toggle"
                    className="akm-btn-ghost h-8 text-[11.5px]"
                >
                    {editing ? "Anulo" : "Modifiko"}
                </button>
            </div>
            {editing ? (
                <ConfigEditor cfg={cfg} onSave={save} saving={saving} />
            ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="dunning-config-view">
                    <Field label="Përpjekje max tarifimi">{cfg.max_attempts}</Field>
                    <Field label="Interval retry (ditë)">{cfg.retry_interval_days}</Field>
                    <Field label="Kujtesa para afatit (ditë)">{cfg.reminder_days_before_due}</Field>
                    <Field label="Grace period (ditë)">{cfg.grace_period_days}</Field>
                    <Field label="Restricted pas (ditë)">{cfg.restricted_after_days}</Field>
                    <Field label="Suspend pas (ditë)">{cfg.suspend_after_days}</Field>
                    <Field label="Statusi">{cfg.enabled ? "Aktiv" : "Çaktivizuar"}</Field>
                </div>
            )}
        </div>
    );
}

function ConfigEditor({ cfg, onSave, saving }) {
    const [form, setForm] = useState(cfg);
    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const nInput = (k, label) => (
        <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
            <input
                type="number"
                value={form[k] ?? ""}
                onChange={(e) => setField(k, parseInt(e.target.value || "0", 10))}
                data-testid={`dunning-cfg-${k}`}
                className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-[13px] font-normal normal-case"
                min={0}
                max={365}
            />
        </label>
    );
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {nInput("max_attempts", "Përpjekje max")}
                {nInput("retry_interval_days", "Interval retry (d)")}
                {nInput("reminder_days_before_due", "Kujtesa para afatit (d)")}
                {nInput("grace_period_days", "Grace period (d)")}
                {nInput("restricted_after_days", "Restricted pas (d)")}
                {nInput("suspend_after_days", "Suspend pas (d)")}
                <label className="col-span-2 flex items-center gap-2 pt-4 text-[12px]">
                    <input
                        type="checkbox"
                        checked={!!form.enabled}
                        onChange={(e) => setField("enabled", e.target.checked)}
                        data-testid="dunning-cfg-enabled"
                    />
                    Aktiv (dunning automatik)
                </label>
            </div>
            <div className="flex justify-end gap-2">
                <button
                    onClick={() => onSave(form)}
                    disabled={saving}
                    data-testid="dunning-cfg-save"
                    className="akm-btn-primary text-[12px]"
                >
                    {saving ? "Duke ruajtur..." : "Ruaj konfigurimin"}
                </button>
            </div>
        </div>
    );
}
